/**
 * The governed-settings kernel (W6-SETTINGS-CORE, D393, ADR-0060): the Settings Standard in code.
 *
 * A governed setting is an ordinary S008 `PolicySetting` (versioned, immutable per version, effective-dated, folded
 * platform → country → market) whose CHANGES go through this kernel instead of a single operator's publish. It
 * generalizes the D342 session-revocation TTL kernel (`governed-setting.ts`, which now runs on this kernel through an
 * adapter) along four axes:
 *
 * - **Typed values.** A {@link SettingDefinition} carries the key's parser/validator (its existing builder), the scopes a
 *   value may be published at, the override mode and constraint class every version carries, the four-eyes rule, the
 *   permission words and the stable reason codes its reader emits. Values are JSON (the jsonb `policy_settings` value).
 * - **Scope.** A change targets one BUCKET: `(key, scope, scopeRef)` with scope `platform`, `country` (`XX`), `region`
 *   (the state, an ISO 3166-2 code `XX-YY`, ADR-0124) or `market` (`xx:slug`). Versions are monotonic per bucket; the base a change is decided against is the bucket's.
 * - **Four eyes.** `ALWAYS`: every change is held `PENDING_REVIEW` until a DIFFERENT operator approves it; approving
 *   publishes the version AND its `policy_setting_approvals` attestation (maker = requester, checker = approver, bound
 *   to the content hash), so attested (D366) readers see it. `LOOSENING_ONLY` (the D342 TTL): a tightening applies at
 *   once and a loosening waits for the checker.
 * - **Time.** An `ALWAYS` change may be SCHEDULED: it carries a future `effectiveFrom`, and approving it publishes a
 *   version effective at that instant. A pending change whose scheduled instant passes before a review EXPIRES (the
 *   next intent on that bucket records it). One-step rollback: an operator restores the version immediately preceding
 *   the effective one at once (a new version copying it, pinned by its content hash), and a second operator reviews it
 *   afterwards; restoring any other version is an ordinary four-eyes change.
 * - **Return to inherit (D856, LT-002 / L6b-018).** A published value can never be un-published (versions are immutable and
 *   hash-bound), so a country, state or market that no longer wants its own value ends the bucket with an `END` change: held
 *   `PENDING_REVIEW` for a different operator, and approving it publishes a RETIRED version (a tombstone) from which the
 *   bucket holds no value, so the next broader scope governs again. {@link foldRetiredVersions} is what every reader folds.
 *
 * Lifecycle (ADR-0060): DRAFT (a candidate value validated by {@link parseSettingValue}; never persisted) →
 * PENDING_REVIEW → SCHEDULED → ACTIVE → SUPERSEDED, plus REJECTED, WITHDRAWN and EXPIRED. The persisted revisions are
 * PENDING_REVIEW / APPROVED / APPLIED / REJECTED / WITHDRAWN / SUPERSEDED / EXPIRED / REVIEWED; SCHEDULED, ACTIVE and
 * SUPERSEDED of a PUBLISHED version are derived from effective-dating ({@link settingVersionStatus}), so no clock job
 * moves them. A change decided against a base that moved is refused `STALE_BASE` and recorded SUPERSEDED.
 *
 * Everything here is pure and deterministic. The in-memory store runs these decisions; PostgreSQL runs the equivalent
 * state machine in the m0124-settings intent functions from locked database state and its own clock. Neutral: keys,
 * scopes and values are data; nothing is country-specific.
 */
import { fourEyes } from '@vello/rbac';
import { canonicalPolicyValue, hashPolicyVersion } from './policy-approval.js';
import { policySetting } from './policy.js';
// ---- vocabulary -----------------------------------------------------------------------------------------------------
/**
 * The scopes a governed setting may be published at (the operational tiers of ADR-0032 / ADR-0045 / ADR-0124), BROADEST FIRST:
 * the most specific effective version wins, so a market's value beats its state's, which beats its country's, which beats the
 * platform's (`@vello/config` `resolvePolicy` folds in this order). The state (`region`) is the second level of the operating
 * hierarchy Global > Country > State > City; a market without a registered state simply skips that layer.
 *
 * A KEY opts into a scope through its definition's `allowedScopes`: listing `region` there is a promise that every reader of the
 * key folds the state layer (see {@link REGION_CAPABLE_SETTING_KEYS}); a key that does not list it refuses a state bucket
 * (`SCOPE_NOT_ALLOWED`) rather than accept a value no reader would honour.
 */
export const SETTING_SCOPES = ['platform', 'country', 'region', 'market'];
const COUNTRY_REF = /^[A-Z]{2}$/;
/** An ISO 3166-2 first-level subdivision code (`BR-MG`): the country, a hyphen, 1-3 upper-case alphanumerics (the shape of `RegionCode`). */
export const REGION_REF = /^[A-Z]{2}-[A-Z0-9]{1,3}$/;
const MARKET_REF = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/;
const UTC_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;
/** The stored `scope_ref` of a target: `''` for platform. */
export function settingBucketRef(target) {
    return target.scope === 'platform' ? '' : (target.scopeRef ?? '');
}
/** Why a target cannot hold a version of this key, or undefined when it can. */
export function settingTargetIssue(meta, target) {
    if (!SETTING_SCOPES.includes(target.scope) || !meta.allowedScopes.includes(target.scope))
        return 'SCOPE_NOT_ALLOWED';
    if (target.scope === 'platform')
        return target.scopeRef === undefined || target.scopeRef === '' ? undefined : 'INVALID_SCOPE_REF';
    if (target.scope === 'country')
        return target.scopeRef !== undefined && COUNTRY_REF.test(target.scopeRef) ? undefined : 'INVALID_SCOPE_REF';
    if (target.scope === 'region')
        return target.scopeRef !== undefined && REGION_REF.test(target.scopeRef) ? undefined : 'INVALID_SCOPE_REF';
    return target.scopeRef !== undefined && MARKET_REF.test(target.scopeRef) ? undefined : 'INVALID_SCOPE_REF';
}
/** Validate a candidate value (a DRAFT) for a target: the target's shape first, then the definition's parser. */
export function parseSettingValue(def, raw, target) {
    const issue = settingTargetIssue(def, target);
    if (issue !== undefined)
        return { ok: false, code: issue };
    return def.parse(raw, target);
}
/** Two values are the same setting value when their canonical serializations (the content hash's) are equal. */
export function sameSettingValue(a, b) {
    return canonicalPolicyValue(a) === canonicalPolicyValue(b);
}
// ---- actors ---------------------------------------------------------------------------------------------------------
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A seed identity (`seed:policy`, `seed:governance`): only the seed path (never an operator intent) records one. */
export const SEED_ACTOR = /^seed:[a-z0-9._:-]{1,60}$/;
/** The canonical (lower-case) form of an operator UUID, or undefined when it is not one (D342 r3 P2-4). */
export function canonicalOperatorId(raw) {
    return typeof raw === 'string' && UUID.test(raw) ? raw.toLowerCase() : undefined;
}
function operatorOf(raw) {
    const actor = canonicalOperatorId(raw);
    if (actor === undefined)
        throw new RangeError('governed setting: the actor must be an operator UUID');
    return actor;
}
const sameOperator = (recorded, actor) => (canonicalOperatorId(recorded) ?? recorded) === actor;
const MAX_REASON_LENGTH = 500;
/** A trimmed, bounded reason, or undefined when blank. */
export function settingReason(raw) {
    if (typeof raw !== 'string')
        return undefined;
    const r = raw.trim();
    return r === '' ? undefined : r.slice(0, MAX_REASON_LENGTH);
}
// ---- versions of a bucket -------------------------------------------------------------------------------------------
function inBucket(s, key, scope, ref) {
    return s.key === key && s.scope === scope && (s.scopeRef ?? '') === ref;
}
function isEffectiveAt(s, atMs) {
    const from = Date.parse(s.effectiveFrom);
    const to = s.effectiveTo === undefined ? Infinity : Date.parse(s.effectiveTo);
    return from <= atMs && atMs < to;
}
/** The highest version published in a bucket (0 when none) — scheduled future versions included. */
export function latestBucketVersion(settings, key, target) {
    const ref = settingBucketRef(target);
    return settings.filter((s) => inBucket(s, key, target.scope, ref)).reduce((max, s) => Math.max(max, s.version), 0);
}
/** The highest effective version of a bucket at `at`, a retired one (tombstone) included. */
function highestEffective(settings, key, target, at) {
    const ref = settingBucketRef(target);
    const atMs = Date.parse(at);
    let winner;
    for (const s of settings) {
        if (!inBucket(s, key, target.scope, ref) || !isEffectiveAt(s, atMs))
            continue;
        if (winner === undefined || s.version > winner.version)
            winner = s;
    }
    return winner;
}
/**
 * The version in force in a bucket at `at`: the highest effective version (the S008 per-bucket dedupe). When that version
 * is a RETIRED one (a governed "return to inherit", D856) the bucket holds no value: undefined, exactly as if it were unset.
 */
export function effectiveBucketVersion(settings, key, target, at) {
    const winner = highestEffective(settings, key, target, at);
    return winner?.retired === true ? undefined : winner;
}
/**
 * What a reader folds (D856): every RETIRED version removed, and every earlier version of its bucket closed at the instant
 * the tombstone takes effect (`effectiveTo`), so the bucket holds no value from then on and the next broader scope governs
 * again - while a resolution made BEFORE that instant still finds the value that governed then (snapshots stay
 * reproducible). A version published after the tombstone is untouched. The input is not changed; a list without a
 * tombstone is returned as it is, so a key that never ended a bucket pays nothing.
 */
export function foldRetiredVersions(settings) {
    if (!settings.some((s) => s.retired === true))
        return settings;
    const bucketId = (s) => `${s.key}|${s.scope}|${s.scopeRef ?? ''}`;
    const stops = new Map();
    for (const s of settings) {
        if (s.retired !== true)
            continue;
        const list = stops.get(bucketId(s)) ?? [];
        list.push({ version: s.version, from: s.effectiveFrom });
        stops.set(bucketId(s), list);
    }
    const out = [];
    for (const s of settings) {
        if (s.retired === true)
            continue;
        let to = s.effectiveTo;
        for (const stop of stops.get(bucketId(s)) ?? []) {
            if (stop.version > s.version && (to === undefined || Date.parse(stop.from) < Date.parse(to)))
                to = stop.from;
        }
        out.push(to === s.effectiveTo ? s : { ...s, effectiveTo: to });
    }
    return out;
}
/**
 * W7-GOVERNED-OTP-KEYS (D410): the bucket that would govern the same value as a new version of `target` — or undefined.
 *
 * The new version is the highest of its bucket, so it governs `target` from `effectiveFrom` on. Another bucket of the
 * same key and scope collides when, at ANY instant from `effectiveFrom` on, the version IT folds (its highest effective
 * version) carries the same value (canonically equal). The instants examined are `effectiveFrom` and every boundary of
 * the other buckets' versions after it — the only instants at which what they fold can change. A version the reader
 * could not verify carries a placeholder value, so it never matches; the database twin (m0131
 * `vello.setting_value_collision`) compares the STORED values, under a per-key lock.
 */
export function settingValueCollision(settings, key, target, value, effectiveFrom) {
    const fromMs = Date.parse(effectiveFrom);
    if (Number.isNaN(fromMs))
        return undefined;
    const ref = settingBucketRef(target);
    const others = settings.filter((s) => s.key === key && s.scope === target.scope && (s.scopeRef ?? '') !== ref);
    const instants = new Set([fromMs]);
    for (const o of others) {
        const from = Date.parse(o.effectiveFrom);
        if (from > fromMs)
            instants.add(from);
        if (o.effectiveTo !== undefined && Date.parse(o.effectiveTo) > fromMs)
            instants.add(Date.parse(o.effectiveTo));
    }
    const refs = [...new Set(others.map((o) => o.scopeRef ?? ''))].sort();
    for (const at of [...instants].sort((a, b) => a - b)) {
        for (const other of refs) {
            let winner;
            for (const o of others) {
                if ((o.scopeRef ?? '') !== other || !isEffectiveAt(o, at))
                    continue;
                if (winner === undefined || o.version > winner.version)
                    winner = o;
            }
            if (winner !== undefined && sameSettingValue(winner.value, value))
                return other;
        }
    }
    return undefined;
}
export function settingVersionStatus(settings, version, now) {
    const nowMs = Date.parse(now);
    if (Date.parse(version.effectiveFrom) > nowMs)
        return 'SCHEDULED';
    const target = { scope: version.scope, ...(version.scopeRef !== undefined ? { scopeRef: version.scopeRef } : {}) };
    // D856: a bucket whose highest effective version is a tombstone holds no value: the tombstone and every version it
    // closed are ENDED; a version published after it governs again like any other.
    const top = highestEffective(settings, version.key, target, now);
    if (top?.retired === true && top.version >= version.version)
        return 'ENDED';
    const current = effectiveBucketVersion(settings, version.key, target, now);
    if (current !== undefined && current.version === version.version)
        return 'ACTIVE';
    if (version.effectiveTo !== undefined && Date.parse(version.effectiveTo) <= nowMs && (current === undefined || current.version < version.version))
        return 'ENDED';
    return 'SUPERSEDED';
}
export function settingBase(def, settings, target, at) {
    const eff = effectiveBucketVersion(settings, def.key, target, at);
    if (eff === undefined)
        return { source: 'DEFAULT', ...(def.defaultValue !== undefined ? { value: def.defaultValue } : {}) };
    const contentHash = hashPolicyVersion(eff);
    if (def.fourEyes === 'LOOSENING_ONLY' && !def.parse(eff.value, target).ok) {
        return { source: 'INVALID', version: eff.version, ...(def.strictest !== undefined ? { value: def.strictest } : {}), contentHash };
    }
    return { source: 'POLICY', version: eff.version, value: eff.value, contentHash };
}
/**
 * Whether a recorded base is still the base. A version is immutable, so (source, version, value) identifies it; the
 * content hash is compared too whenever the record carries one (every kernel record does; a D342 TTL record, converted
 * by the adapter, does not).
 */
export function sameSettingBase(a, b) {
    return (a !== undefined
        && a.source === b.source
        && a.version === b.version
        && (a.contentHash === undefined || a.contentHash === b.contentHash)
        && sameSettingValue(a.value ?? null, b.value ?? null));
}
const describeBase = (b) => b === undefined ? 'unrecorded' : `${b.source}${b.version !== undefined ? ` v${b.version}` : ''}${b.value !== undefined ? ` = ${JSON.stringify(b.value)}` : ''}`;
// ---- change records -------------------------------------------------------------------------------------------------
export const SETTING_CHANGE_KINDS = ['CHANGE', 'ROLLBACK', 'SEED', 'END'];
/** The persisted revision statuses (SCHEDULED / ACTIVE / SUPERSEDED of a published version are derived). */
export const SETTING_REVISION_STATUSES = ['PENDING_REVIEW', 'APPROVED', 'APPLIED', 'REJECTED', 'WITHDRAWN', 'SUPERSEDED', 'EXPIRED', 'REVIEWED'];
/** Stable reason codes carried on every revision (the "why" of each governance decision). */
export const SETTING_REVISION_REASON_CODES = [
    'SETTING_CHANGE_PENDING_REVIEW',
    'SETTING_CHANGE_APPROVED',
    'SETTING_TIGHTENED_APPLIED',
    'SETTING_LOOSENING_PENDING_APPROVAL',
    'SETTING_LOOSENING_APPROVED_APPLIED',
    'SETTING_CHANGE_REJECTED',
    'SETTING_CHANGE_WITHDRAWN',
    'SETTING_CHANGE_SUPERSEDED',
    'SETTING_CHANGE_EXPIRED',
    'SETTING_ROLLBACK_APPLIED',
    'SETTING_ROLLBACK_PENDING_REVIEW',
    'SETTING_ROLLBACK_APPROVED',
    'SETTING_ROLLBACK_REVIEWED',
    'SETTING_SEED_SUBMITTED',
    'SETTING_SEED_APPROVED',
    'SETTING_END_PENDING_REVIEW',
    'SETTING_END_APPROVED',
];
/** Keep only the latest revision of each change, newest request first. */
export function latestSettingRevisions(records) {
    const byId = new Map();
    for (const r of records) {
        const prev = byId.get(r.id);
        if (prev === undefined || r.revision > prev.revision)
            byId.set(r.id, r);
    }
    return [...byId.values()].sort((a, b) => b.requestedAt.localeCompare(a.requestedAt) || b.id.localeCompare(a.id));
}
const bucketOf = (c) => `${c.key}|${c.scope}|${c.scopeRef}`;
const bucketKey = (key, target) => `${key}|${target.scope}|${settingBucketRef(target)}`;
/** The pending changes of one bucket (latest revision PENDING_REVIEW). */
export function pendingSettingChanges(state, key, target) {
    const b = bucketKey(key, target);
    return state.changes.filter((c) => bucketOf(c) === b && c.status === 'PENDING_REVIEW');
}
/** A pending change whose requested schedule has already passed can no longer be reviewed: it has expired. */
export function isPastSchedule(change, at) {
    return change.effectiveFrom !== undefined && Date.parse(change.effectiveFrom) <= Date.parse(at);
}
export function settingChangeLifecycle(change, settings, now) {
    if (change.status === 'PENDING_REVIEW')
        return isPastSchedule(change, now) ? 'EXPIRED' : 'PENDING_REVIEW';
    if (change.status === 'REJECTED' || change.status === 'WITHDRAWN' || change.status === 'EXPIRED')
        return change.status;
    if (change.status === 'SUPERSEDED')
        return 'SUPERSEDED';
    const published = settings.find((s) => s.key === change.key && s.scope === change.scope && (s.scopeRef ?? '') === change.scopeRef && s.version === change.appliedVersion);
    return published === undefined ? 'SUPERSEDED' : settingVersionStatus(settings, published, now);
}
function targetOfChange(c) {
    return { scope: c.scope, ...(c.scopeRef !== '' ? { scopeRef: c.scopeRef } : {}) };
}
function publishVersion(meta, state, target, value, publishId, effectiveFrom, at, retired = false) {
    const ref = settingBucketRef(target);
    return {
        id: publishId,
        createdAt: at,
        setting: policySetting({
            key: meta.key,
            scope: target.scope,
            ...(ref !== '' ? { scopeRef: ref } : {}),
            value,
            version: latestBucketVersion(state.settings, meta.key, target) + 1,
            effectiveFrom,
            mode: meta.mode,
            ...(meta.constraintClass !== undefined ? { constraintClass: meta.constraintClass } : {}),
            ...(retired ? { retired: true } : {}),
        }),
    };
}
/** Record every pending change of the bucket whose schedule has passed as EXPIRED (by the actor whose intent found it). */
function expireStale(state, key, target, actor, at) {
    return pendingSettingChanges(state, key, target)
        .filter((c) => isPastSchedule(c, at))
        .map((c) => ({
        ...c, revision: c.revision + 1, status: 'EXPIRED', reasonCode: 'SETTING_CHANGE_EXPIRED',
        resolvedBy: actor, resolvedAt: at, resolutionReason: 'the scheduled instant passed before a review',
    }));
}
/** Apply a list of new revisions to an in-memory view of the changes (latest revision each). */
function withRevisions(state, revisions) {
    return revisions.length === 0 ? state : { settings: state.settings, changes: latestSettingRevisions([...state.changes, ...revisions]) };
}
function attestationOf(setting, change, approver, at, reason, selfApproval) {
    return {
        key: setting.key,
        scope: setting.scope,
        scopeRef: setting.scopeRef ?? '',
        version: setting.version,
        contentHash: hashPolicyVersion(setting),
        submittedBy: change.requestedBy,
        submittedAt: change.requestedAt,
        submissionReason: change.requestReason,
        approvedBy: approver,
        approvedAt: at,
        approvalReason: reason,
        ...(selfApproval !== undefined ? { selfApproval } : {}),
    };
}
/**
 * Propose a new value for one bucket. `ALWAYS`: held PENDING_REVIEW for a second operator (one pending change per
 * bucket; a pending change past its schedule is recorded EXPIRED first). `LOOSENING_ONLY`: a tightening applies at once
 * and supersedes any pending loosening; a loosening waits; while the stored value is unreadable only a repair to the
 * strictest value applies at once (D342 r3 P1-1).
 */
export function proposeSettingChange(def, state, input) {
    const actor = operatorOf(input.actor);
    const target = input.target;
    const targetIssue = settingTargetIssue(def, target);
    if (targetIssue !== undefined)
        return { ok: false, code: targetIssue };
    const parsed = def.parse(input.value, target);
    if (!parsed.ok)
        return { ok: false, code: parsed.code, ...(parsed.detail !== undefined ? { detail: parsed.detail } : {}) };
    const reason = settingReason(input.reason);
    if (reason === undefined)
        return { ok: false, code: 'REASON_REQUIRED' };
    const ref = settingBucketRef(target);
    const baseVersion = latestBucketVersion(state.settings, def.key, target);
    if (def.fourEyes === 'LOOSENING_ONLY') {
        if (input.effectiveFrom !== undefined)
            return { ok: false, code: 'SCHEDULE_NOT_SUPPORTED' };
        if (def.classify === undefined)
            throw new Error(`${def.key}: a LOOSENING_ONLY setting needs a classifier`);
        const base = settingBase(def, state.settings, target, input.at);
        const direction = def.classify(base.value, parsed.value);
        // While the stored value is unreadable the base is the strictest value: re-writing it is a genuine repair (never
        // NO_CHANGE), and every other value loosens what some replica may still enforce.
        const classified = base.source === 'INVALID' && direction === 'UNCHANGED' ? 'TIGHTEN' : direction;
        if (classified === 'UNCHANGED')
            return { ok: false, code: 'NO_CHANGE' };
        const pending = pendingSettingChanges(state, def.key, target);
        const common = {
            id: input.id, key: def.key, scope: target.scope, scopeRef: ref, revision: 1, kind: 'CHANGE', fourEyes: def.fourEyes,
            direction: classified, ...(base.value !== undefined ? { fromValue: base.value } : {}), toValue: parsed.value, baseVersion, base,
            requestedBy: actor, requestedAt: input.at, requestReason: reason,
        };
        if (classified === 'LOOSEN') {
            if (pending.length > 0)
                return { ok: false, code: 'PENDING_CHANGE_EXISTS' };
            const change = { ...common, status: 'PENDING_REVIEW', reasonCode: 'SETTING_LOOSENING_PENDING_APPROVAL' };
            return { ok: true, change, plan: { changes: [change] } };
        }
        const publish = publishVersion(def, state, target, parsed.value, input.publishId, input.at, input.at);
        const change = {
            ...common, status: 'APPLIED', reasonCode: 'SETTING_TIGHTENED_APPLIED',
            appliedVersion: publish.setting.version, appliedAt: input.at, appliedEffectiveFrom: input.at, contentHash: hashPolicyVersion(publish.setting),
        };
        // A pending loosening was decided against the value this tightening replaces — it can no longer be approved.
        const superseded = pending.map((c) => ({
            ...c, revision: c.revision + 1, status: 'SUPERSEDED', reasonCode: 'SETTING_CHANGE_SUPERSEDED',
            resolvedBy: actor, resolvedAt: input.at, resolutionReason: `superseded by change ${input.id}`,
        }));
        return { ok: true, change, plan: { changes: [change, ...superseded], publish } };
    }
    // ALWAYS: schedule, expiry, one pending per bucket, then the pinned base.
    if (input.effectiveFrom !== undefined) {
        if (!UTC_INSTANT.test(input.effectiveFrom) || Number.isNaN(Date.parse(input.effectiveFrom)) || Date.parse(input.effectiveFrom) <= Date.parse(input.at)) {
            return { ok: false, code: 'INVALID_SCHEDULE' };
        }
    }
    const expired = expireStale(state, def.key, target, actor, input.at);
    const current = withRevisions(state, expired);
    const refuse = (code) => (expired.length > 0 ? { ok: false, code, plan: { changes: expired } } : { ok: false, code });
    if (pendingSettingChanges(current, def.key, target).length > 0)
        return refuse('PENDING_CHANGE_EXISTS');
    const base = settingBase(def, current.settings, target, input.at);
    if (base.source === 'POLICY' && sameSettingValue(base.value, parsed.value) && input.effectiveFrom === undefined && baseVersion === base.version) {
        return refuse('NO_CHANGE');
    }
    // D410: a value another bucket governs at an instant this change would cover (checked again at approval).
    if (def.uniqueValueRefusal !== undefined && settingValueCollision(current.settings, def.key, target, parsed.value, input.effectiveFrom ?? input.at) !== undefined) {
        return refuse(def.uniqueValueRefusal);
    }
    const change = {
        id: input.id, key: def.key, scope: target.scope, scopeRef: ref, revision: 1, kind: 'CHANGE', fourEyes: 'ALWAYS',
        ...(base.value !== undefined ? { fromValue: base.value } : {}), toValue: parsed.value, baseVersion, base,
        ...(input.effectiveFrom !== undefined ? { effectiveFrom: input.effectiveFrom } : {}),
        status: 'PENDING_REVIEW', reasonCode: 'SETTING_CHANGE_PENDING_REVIEW', requestedBy: actor, requestedAt: input.at, requestReason: reason,
    };
    return { ok: true, change, plan: { changes: [...expired, change] } };
}
function findPending(state, changeId) {
    const change = state.changes.find((c) => c.id === changeId);
    if (change === undefined)
        return 'CHANGE_NOT_FOUND';
    if (change.status !== 'PENDING_REVIEW')
        return 'NOT_PENDING';
    return change;
}
function expiredRevision(found, actor, at) {
    const change = {
        ...found, revision: found.revision + 1, status: 'EXPIRED', reasonCode: 'SETTING_CHANGE_EXPIRED',
        resolvedBy: actor, resolvedAt: at, resolutionReason: 'the scheduled instant passed before a review',
    };
    return { ok: false, code: 'EXPIRED', change, plan: { changes: [change] } };
}
/**
 * Approve a pending change (four eyes on canonical operator ids). The approver must not be the requester (D868: unless
 * `input.selfApproval` — a GLOBAL super administrator — which the revision and its attestation record). A change past
 * its schedule EXPIRES. The base must be exactly the one the request was decided against (same latest version, same
 * effective version and content), otherwise the change is refused `STALE_BASE` and recorded SUPERSEDED. The value is
 * re-validated. `ALWAYS`: the version is published effective at the schedule (or now) together with its attestation.
 */
export function approveSettingChange(def, state, input) {
    const actor = operatorOf(input.actor);
    const found = findPending(state, input.changeId);
    if (typeof found === 'string')
        return { ok: false, code: found };
    if (found.key !== def.key)
        return { ok: false, code: 'CHANGE_NOT_FOUND' };
    const reason = settingReason(input.reason);
    if (reason === undefined)
        return { ok: false, code: 'REASON_REQUIRED' };
    const eyes = fourEyes(sameOperator(found.requestedBy, actor), input.selfApproval);
    if (!eyes.ok)
        return { ok: false, code: 'SELF_APPROVAL' };
    if (isPastSchedule(found, input.at))
        return expiredRevision(found, actor, input.at);
    const target = targetOfChange(found);
    const base = settingBase(def, state.settings, target, input.at);
    const latest = latestBucketVersion(state.settings, def.key, target);
    if (latest !== found.baseVersion || !sameSettingBase(found.base, base)) {
        const superseded = {
            ...found, revision: found.revision + 1, status: 'SUPERSEDED', reasonCode: 'SETTING_CHANGE_SUPERSEDED',
            resolvedBy: actor, resolvedAt: input.at,
            resolutionReason: `effective base changed since the request (was ${describeBase(found.base)} at v${found.baseVersion}, now ${describeBase(base)} at v${latest})`,
        };
        return { ok: false, code: 'STALE_BASE', change: superseded, plan: { changes: [superseded] } };
    }
    // Re-check the value at approval time: a deploy may have narrowed the definition since the request. An END retires the
    // value it names (it may even be one today's validator would refuse), so there is nothing to re-check.
    const ending = found.kind === 'END';
    const parsed = ending ? { ok: true, value: found.toValue } : def.parse(found.toValue, target);
    if (!parsed.ok)
        return { ok: false, code: parsed.code, ...(parsed.detail !== undefined ? { detail: parsed.detail } : {}) };
    if (found.kind === 'ROLLBACK' && found.rollbackOf !== undefined) {
        const restored = state.settings.find((s) => inBucket(s, def.key, target.scope, found.scopeRef) && s.version === found.rollbackOf?.version);
        if (restored === undefined || hashPolicyVersion(restored) !== found.rollbackOf.contentHash)
            return { ok: false, code: 'ROLLBACK_TARGET_MISMATCH' };
    }
    const effectiveFrom = found.effectiveFrom ?? input.at;
    // D410: another bucket may have taken the value while this change waited — refused, the change stays pending.
    if (!ending && def.uniqueValueRefusal !== undefined && settingValueCollision(state.settings, def.key, target, parsed.value, effectiveFrom) !== undefined) {
        return { ok: false, code: def.uniqueValueRefusal };
    }
    const publish = publishVersion(def, state, target, parsed.value, input.publishId, effectiveFrom, input.at, ending);
    const contentHash = hashPolicyVersion(publish.setting);
    const loosening = def.fourEyes === 'LOOSENING_ONLY';
    const change = {
        ...found, revision: found.revision + 1, status: loosening ? 'APPLIED' : 'APPROVED',
        reasonCode: loosening ? 'SETTING_LOOSENING_APPROVED_APPLIED' : found.kind === 'ROLLBACK' ? 'SETTING_ROLLBACK_APPROVED' : ending ? 'SETTING_END_APPROVED' : 'SETTING_CHANGE_APPROVED',
        resolvedBy: actor, resolvedAt: input.at, resolutionReason: reason,
        appliedVersion: publish.setting.version, appliedAt: input.at, appliedEffectiveFrom: effectiveFrom, contentHash,
        ...(eyes.selfApproval !== undefined ? { selfApproval: eyes.selfApproval } : {}),
    };
    return {
        ok: true,
        change,
        plan: { changes: [change], publish, ...(loosening ? {} : { attestation: attestationOf(publish.setting, found, actor, input.at, reason, eyes.selfApproval) }) },
    };
}
/** Reject a pending change. When the requester (by canonical id) resolves their own request it is recorded WITHDRAWN. */
export function rejectSettingChange(state, input) {
    const actor = operatorOf(input.actor);
    const found = findPending(state, input.changeId);
    if (typeof found === 'string')
        return { ok: false, code: found };
    const reason = settingReason(input.reason);
    if (reason === undefined)
        return { ok: false, code: 'REASON_REQUIRED' };
    if (isPastSchedule(found, input.at))
        return expiredRevision(found, actor, input.at);
    const withdrawn = sameOperator(found.requestedBy, actor);
    const change = {
        ...found, revision: found.revision + 1,
        status: withdrawn ? 'WITHDRAWN' : 'REJECTED',
        reasonCode: withdrawn ? 'SETTING_CHANGE_WITHDRAWN' : 'SETTING_CHANGE_REJECTED',
        resolvedBy: actor, resolvedAt: input.at, resolutionReason: reason,
    };
    return { ok: true, change, plan: { changes: [change] } };
}
/** Withdraw a pending change: only its requester may. */
export function withdrawSettingChange(state, input) {
    const actor = operatorOf(input.actor);
    const found = findPending(state, input.changeId);
    if (typeof found === 'string')
        return { ok: false, code: found };
    if (!sameOperator(found.requestedBy, actor))
        return { ok: false, code: 'NOT_REQUESTER' };
    return rejectSettingChange(state, input);
}
/**
 * D856 (LT-002 / L6b-018): propose that a bucket hold NO value any more, so the next broader scope governs again. Always a
 * four-eyes proposal (`PENDING_REVIEW`); approving it publishes a retired version effective at the approval. Refused:
 * `END_NOT_SUPPORTED` for a `LOOSENING_ONLY` key and for a key that guards a unique value across buckets (D410),
 * `PLATFORM_CANNOT_INHERIT` for the platform bucket (there is no broader scope), `NOTHING_TO_END` while no version governs
 * the bucket (nothing set, or already ended), `SCHEDULED_VERSION_EXISTS` while a version waits for its instant (ending
 * would silently cancel it). The value it names is the one in force, so the trail states what was ended.
 */
export function endSettingBucket(def, state, input) {
    const actor = operatorOf(input.actor);
    const target = input.target;
    if (def.fourEyes !== 'ALWAYS' || def.uniqueValueRefusal !== undefined)
        return { ok: false, code: 'END_NOT_SUPPORTED' };
    const targetIssue = settingTargetIssue(def, target);
    if (targetIssue !== undefined)
        return { ok: false, code: targetIssue };
    if (target.scope === 'platform')
        return { ok: false, code: 'PLATFORM_CANNOT_INHERIT' };
    const reason = settingReason(input.reason);
    if (reason === undefined)
        return { ok: false, code: 'REASON_REQUIRED' };
    const expired = expireStale(state, def.key, target, actor, input.at);
    const current = withRevisions(state, expired);
    const refuse = (code) => (expired.length > 0 ? { ok: false, code, plan: { changes: expired } } : { ok: false, code });
    if (pendingSettingChanges(current, def.key, target).length > 0)
        return refuse('PENDING_CHANGE_EXISTS');
    const eff = effectiveBucketVersion(current.settings, def.key, target, input.at);
    if (eff === undefined)
        return refuse('NOTHING_TO_END');
    const latest = latestBucketVersion(current.settings, def.key, target);
    if (latest !== eff.version)
        return refuse('SCHEDULED_VERSION_EXISTS');
    const base = settingBase(def, current.settings, target, input.at);
    const change = {
        id: input.id, key: def.key, scope: target.scope, scopeRef: settingBucketRef(target), revision: 1, kind: 'END', fourEyes: 'ALWAYS',
        fromValue: eff.value, toValue: eff.value, baseVersion: latest, base,
        status: 'PENDING_REVIEW', reasonCode: 'SETTING_END_PENDING_REVIEW', requestedBy: actor, requestedAt: input.at, requestReason: reason,
    };
    return { ok: true, change, plan: { changes: [...expired, change] } };
}
/**
 * Restore an earlier version as a NEW version copying its value (ADR-0060). The version immediately preceding the
 * effective one is restored at once by one operator (`APPLIED`, reviewed afterwards by a second one, and every pending
 * change of the bucket is superseded); any other version is an ordinary four-eyes change (`PENDING_REVIEW`). A key whose
 * metadata sets {@link SettingMetadata.rollbackRequiresReview} (the two OTP keys, W7O-03) never takes the one-step
 * shortcut: every rollback of it is `PENDING_REVIEW`. The target is pinned by its content hash: a hash that does not
 * match what is stored is refused.
 */
export function rollbackSetting(def, state, input) {
    const actor = operatorOf(input.actor);
    if (def.fourEyes !== 'ALWAYS')
        return { ok: false, code: 'ROLLBACK_NOT_SUPPORTED' };
    const target = input.target;
    const targetIssue = settingTargetIssue(def, target);
    if (targetIssue !== undefined)
        return { ok: false, code: targetIssue };
    const reason = settingReason(input.reason);
    if (reason === undefined)
        return { ok: false, code: 'REASON_REQUIRED' };
    const ref = settingBucketRef(target);
    const expired = expireStale(state, def.key, target, actor, input.at);
    const current = withRevisions(state, expired);
    const refuse = (code) => (expired.length > 0 ? { ok: false, code, plan: { changes: expired } } : { ok: false, code });
    const eff = effectiveBucketVersion(current.settings, def.key, target, input.at);
    if (eff === undefined)
        return refuse('NOTHING_TO_ROLL_BACK');
    const restored = current.settings.find((s) => inBucket(s, def.key, target.scope, ref) && s.version === input.toVersion);
    if (restored === undefined)
        return refuse('ROLLBACK_TARGET_NOT_FOUND');
    const restoredHash = hashPolicyVersion(restored);
    if (restoredHash !== input.toContentHash)
        return refuse('ROLLBACK_TARGET_MISMATCH');
    if (restored.version === eff.version)
        return refuse('ROLLBACK_TARGET_IS_CURRENT');
    if (restored.version > eff.version)
        return refuse('ROLLBACK_TARGET_NOT_EARLIER');
    const parsed = def.parse(restored.value, target);
    if (!parsed.ok)
        return refuse(parsed.code);
    const latest = latestBucketVersion(current.settings, def.key, target);
    const base = settingBase(def, current.settings, target, input.at);
    const previous = current.settings.filter((s) => inBucket(s, def.key, target.scope, ref) && s.version < eff.version).reduce((max, s) => Math.max(max, s.version), 0);
    const common = {
        id: input.id, key: def.key, scope: target.scope, scopeRef: ref, kind: 'ROLLBACK', fourEyes: 'ALWAYS',
        fromValue: eff.value, toValue: parsed.value, baseVersion: latest, base,
        rollbackOf: { version: restored.version, contentHash: restoredHash },
        requestedBy: actor, requestedAt: input.at, requestReason: reason,
    };
    // W7O-03 (D414): a key that requires review never takes the one-step shortcut — its rollback is a pending proposal.
    if (restored.version === previous && def.rollbackRequiresReview !== true) {
        // One step back: applied at once, reviewed afterwards. A waiting scheduled version would silently be outranked.
        if (latest !== eff.version)
            return refuse('SCHEDULED_VERSION_EXISTS');
        // D410: restoring a value another bucket governs now (or later) is refused like any other decision would be.
        if (def.uniqueValueRefusal !== undefined && settingValueCollision(current.settings, def.key, target, parsed.value, input.at) !== undefined) {
            return refuse(def.uniqueValueRefusal);
        }
        const publish = publishVersion(def, current, target, parsed.value, input.publishId, input.at, input.at);
        const change = {
            ...common, revision: 1, status: 'APPLIED', reasonCode: 'SETTING_ROLLBACK_APPLIED',
            appliedVersion: publish.setting.version, appliedAt: input.at, appliedEffectiveFrom: input.at, contentHash: hashPolicyVersion(publish.setting),
        };
        const superseded = pendingSettingChanges(current, def.key, target).map((c) => ({
            ...c, revision: c.revision + 1, status: 'SUPERSEDED', reasonCode: 'SETTING_CHANGE_SUPERSEDED',
            resolvedBy: actor, resolvedAt: input.at, resolutionReason: `superseded by rollback ${input.id}`,
        }));
        return { ok: true, change, plan: { changes: [...expired, change, ...superseded], publish } };
    }
    if (pendingSettingChanges(current, def.key, target).length > 0)
        return refuse('PENDING_CHANGE_EXISTS');
    // D410/W7O-03: a value another bucket governs now is refused at once, as a proposal of that value would be (and again
    // at approve, which is when a version is written).
    if (def.uniqueValueRefusal !== undefined && settingValueCollision(current.settings, def.key, target, parsed.value, input.at) !== undefined) {
        return refuse(def.uniqueValueRefusal);
    }
    const change = { ...common, revision: 1, status: 'PENDING_REVIEW', reasonCode: 'SETTING_ROLLBACK_PENDING_REVIEW' };
    return { ok: true, change, plan: { changes: [...expired, change] } };
}
/**
 * The post-review of a one-step rollback: a DIFFERENT operator records that they reviewed it, and the restored version
 * gets its four-eyes attestation (rollback author = maker, reviewer = checker). An objection is a new change. D868: the
 * rollback's author may review it with `input.selfApproval` (a GLOBAL super administrator), recorded on both.
 */
export function reviewSettingRollback(state, input) {
    const actor = operatorOf(input.actor);
    const found = state.changes.find((c) => c.id === input.changeId);
    if (found === undefined)
        return { ok: false, code: 'CHANGE_NOT_FOUND' };
    if (found.kind !== 'ROLLBACK' || found.status !== 'APPLIED' || found.appliedVersion === undefined)
        return { ok: false, code: 'NOT_REVIEWABLE' };
    const reason = settingReason(input.reason);
    if (reason === undefined)
        return { ok: false, code: 'REASON_REQUIRED' };
    const eyes = fourEyes(sameOperator(found.requestedBy, actor), input.selfApproval);
    if (!eyes.ok)
        return { ok: false, code: 'SELF_REVIEW' };
    const published = state.settings.find((s) => s.key === found.key && s.scope === found.scope && (s.scopeRef ?? '') === found.scopeRef && s.version === found.appliedVersion);
    if (published === undefined)
        return { ok: false, code: 'NOT_REVIEWABLE' };
    const change = {
        ...found, revision: found.revision + 1, status: 'REVIEWED', reasonCode: 'SETTING_ROLLBACK_REVIEWED',
        resolvedBy: actor, resolvedAt: input.at, resolutionReason: reason,
        ...(eyes.selfApproval !== undefined ? { selfApproval: eyes.selfApproval } : {}),
    };
    return { ok: true, change, plan: { changes: [change], attestation: attestationOf(published, found, actor, input.at, reason, eyes.selfApproval) } };
}
/**
 * Publish a country pack's version of a governed key through the governed path (the seed boundary): submitted by one
 * seed identity and approved by ANOTHER, recorded as two revisions, written with its attestation. Idempotent: the same
 * version with the same content (a governed one, or a legacy row labelled "sem revisão (seed)") is `ALREADY_PUBLISHED`
 * and nothing is written. The pack version must be the bucket's next one; anything else is refused, never forced.
 */
export function seedSettingVersion(meta, state, input) {
    const s = input.setting;
    const target = { scope: s.scope, ...(s.scopeRef !== undefined ? { scopeRef: s.scopeRef } : {}) };
    const targetIssue = settingTargetIssue(meta, target);
    if (targetIssue !== undefined)
        return { ok: false, code: targetIssue };
    if (!SEED_ACTOR.test(input.maker) || !SEED_ACTOR.test(input.approver) || input.maker === input.approver)
        return { ok: false, code: 'INVALID_SEED_ACTORS' };
    const submissionReason = settingReason(input.submissionReason);
    const approvalReason = settingReason(input.approvalReason);
    if (submissionReason === undefined || approvalReason === undefined)
        return { ok: false, code: 'REASON_REQUIRED' };
    if (s.key !== meta.key || s.mode !== meta.mode || (s.constraintClass ?? null) !== (meta.constraintClass ?? null) || s.effectiveTo !== undefined) {
        return { ok: false, code: 'SEED_SHAPE_MISMATCH' };
    }
    const ref = settingBucketRef(target);
    const existing = state.settings.find((v) => inBucket(v, meta.key, target.scope, ref) && v.version === s.version);
    if (existing !== undefined)
        return hashPolicyVersion(existing) === hashPolicyVersion(s) ? { ok: true, outcome: 'ALREADY_PUBLISHED' } : { ok: false, code: 'SEED_VERSION_CONFLICT' };
    const latest = latestBucketVersion(state.settings, meta.key, target);
    if (latest !== s.version - 1)
        return { ok: false, code: latest >= s.version ? 'SEED_VERSION_SUPERSEDED' : 'SEED_VERSION_GAP' };
    // D410: a pack can never seed a value another bucket governs at an instant its version covers.
    if (meta.uniqueValueRefusal !== undefined && settingValueCollision(state.settings, meta.key, target, s.value, s.effectiveFrom) !== undefined) {
        return { ok: false, code: meta.uniqueValueRefusal };
    }
    const eff = effectiveBucketVersion(state.settings, meta.key, target, input.at);
    const base = eff === undefined
        ? { source: 'DEFAULT', ...(meta.defaultValue !== undefined ? { value: meta.defaultValue } : {}) }
        : { source: 'POLICY', version: eff.version, value: eff.value, contentHash: hashPolicyVersion(eff) };
    const contentHash = hashPolicyVersion(s);
    const submitted = {
        id: input.changeId, key: meta.key, scope: target.scope, scopeRef: ref, revision: 1, kind: 'SEED', fourEyes: 'ALWAYS',
        ...(base.value !== undefined ? { fromValue: base.value } : {}), toValue: s.value, baseVersion: latest, base,
        status: 'PENDING_REVIEW', reasonCode: 'SETTING_SEED_SUBMITTED', requestedBy: input.maker, requestedAt: input.at, requestReason: submissionReason,
    };
    const approved = {
        ...submitted, revision: 2, status: 'APPROVED', reasonCode: 'SETTING_SEED_APPROVED',
        resolvedBy: input.approver, resolvedAt: input.at, resolutionReason: approvalReason,
        appliedVersion: s.version, appliedAt: input.at, appliedEffectiveFrom: s.effectiveFrom, contentHash,
    };
    return {
        ok: true,
        outcome: 'PUBLISHED',
        change: approved,
        plan: { changes: [submitted, approved], publish: { id: input.publishId, setting: s, createdAt: input.at }, attestation: attestationOf(s, submitted, input.approver, input.at, approvalReason) },
    };
}
/**
 * The value an {@link unverifiedSettingSlot} carries: a JSON object no key's reader accepts (not a mode, not a method or
 * category list, not a search timeout), so every resolver takes its own "invalid policy" path — dispatch stays
 * ASSISTED, a payment policy refuses (`PAYMENT_METHODS_POLICY_INVALID`), the desk sells nothing, no search timeout
 * cancels a ride — for exactly the contexts that fold that bucket, and nowhere else.
 */
export const UNVERIFIED_SETTING_VALUE = Object.freeze({ unverifiedSetting: true });
/**
 * Review W6S-02: a version a governed reader cannot verify KEEPS ITS SLOT (its key, bucket, version and effective window,
 * taken from the stored columns) with {@link UNVERIFIED_SETTING_VALUE}. Dropping it would let an older version govern
 * without any review, or turn the bucket into "unset" — for `payment.allowedMethods` the ungoverned pre-D357 behaviour,
 * i.e. fail OPEN. The content hash is the one recomputed from the stored row (for the audit), never pinned by a
 * transaction ({@link settingSnapshotFields} returns nothing for it).
 */
export function unverifiedSettingSlot(slot) {
    return {
        key: slot.key,
        scope: slot.scope,
        ...(slot.scope !== 'platform' && slot.scopeRef !== '' ? { scopeRef: slot.scopeRef } : {}),
        value: UNVERIFIED_SETTING_VALUE,
        version: slot.version,
        effectiveFrom: slot.effectiveFrom,
        ...(slot.effectiveTo !== undefined ? { effectiveTo: slot.effectiveTo } : {}),
        mode: slot.mode,
        ...(slot.constraintClass !== undefined ? { constraintClass: slot.constraintClass } : {}),
        provenance: { origin: 'UNVERIFIED', contentHash: slot.storedContentHash },
    };
}
/**
 * The snapshot fields a transaction records for the version that decided it (ADR-0060: `{value, reasonCode,
 * key@scope:ref#vN, contentHash, changeId}` — value, reason code and version string are the decision's own). The content
 * hash is recomputed from the version itself; the change id is present only for a governed version.
 */
export function settingSnapshotFields(settings, key, scope, scopeRef, version) {
    if (scope === undefined || version === undefined)
        return {};
    const ref = scope === 'platform' ? '' : (scopeRef ?? '');
    const winner = settings.find((s) => s.key === key && s.scope === scope && (s.scopeRef ?? '') === ref && s.version === version);
    // Review W6S-02: nothing verifiable to pin for a version the reader could not verify.
    if (winner === undefined || winner.provenance?.origin === 'UNVERIFIED')
        return {};
    const changeId = winner.provenance?.origin === 'GOVERNED' ? winner.provenance.changeId : undefined;
    return { contentHash: hashPolicyVersion(winner), ...(changeId !== undefined ? { changeId } : {}) };
}
//# sourceMappingURL=setting-kernel.js.map